import { Quote } from "lucide-react";
import type { FounderSettings as FounderSettingsData } from "@/hooks/useHomeSettings";
import founderImg from "@/assets/founder-portrait.png"

export function FounderMessage({ settings }: { settings?: FounderSettingsData | null }) {
  const heading = settings?.heading || "Leadership \u00B7 A Word From Us";
  const title = settings?.title || "Message from the <span class=\"italic text-secondary\">Founder</span>";
  const message = settings?.message || "";
  const authorName = settings?.author_name || "MD Basruddin Ansari";
  const authorTitle = settings?.author_title || "Founder & Managing Director";
  const establishedYear = settings?.established_year || "Est. 2011";
  const location = settings?.location || "Kathmandu, Nepal";

  const defaultMessages = [
    "At our core, we believe that healthcare is not just a service; it\u2019s a responsibility. With decades dedicated to founding medical institutions and serving the community, my vision has always been clear: to create a place where every patient is treated with dignity, every life is valued, and care goes beyond medicine.",
    "This hospital is more than a facility \u2014 it\u2019s a promise to our people. A promise of compassion, innovation, and hope.",
    "Thank you for being a part of this journey.",
  ];

  return (
    <section id="founder-message" className="relative overflow-hidden bg-background py-24">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 top-20 h-80 w-80 rounded-full bg-primary/5 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 bottom-10 h-96 w-96 rounded-full bg-gold/10 blur-3xl"
      />

      <div className="mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="font-display text-xs font-semibold uppercase tracking-[0.22em] text-secondary">
              {heading}
            </p>
            <h2
              className="mt-4 font-display font-space text-4xl font-bold leading-[1.05] text-primary md:text-5xl"
              dangerouslySetInnerHTML={{ __html: title }}
            />

            <div className="relative mt-8 max-w-2xl">
              <Quote
                aria-hidden
                className="absolute -left-2 -top-4 h-14 w-14 text-gold/30"
                strokeWidth={1.5}
              />
              {message ? (
                <div
                  className="space-y-5 pl-2 text-base leading-relaxed text-foreground/80"
                  dangerouslySetInnerHTML={{ __html: message }}
                />
              ) : (
                <div className="space-y-5 pl-2 text-base leading-relaxed text-foreground/80">
                  {defaultMessages.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-8 flex items-center gap-4 border-l-2 border-gold pl-5">
              <div>
                <div className="font-display text-lg font-semibold text-primary">
                  {authorName}
                </div>
                <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {authorTitle}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-md">
              <div
                aria-hidden
                className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-gold/40 via-secondary/20 to-primary/30 opacity-70 blur-lg"
              />
              <div className="absolute -left-4 -top-4 hidden h-24 w-24 rounded-2xl border-2 border-gold/60 lg:block" />
              <div className="absolute -bottom-4 -right-4 hidden h-24 w-24 rounded-2xl border-2 border-primary/40 lg:block" />
              <div className="relative overflow-hidden rounded-[1.75rem] shadow-elegant ring-1 ring-border">
                <img
                  src={founderImg}
                  alt={authorName}
                  className="aspect-[4/5] w-full object-cover"
                  loading="lazy"
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary/30 via-transparent to-transparent"
                />
              </div>
              <div className="absolute -bottom-6 left-6 rounded-xl bg-primary px-5 py-3 text-primary-foreground shadow-soft">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{establishedYear}</p>
                <p className="font-display text-sm font-semibold">{location}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
